// Single source of truth for fit-tier labels + the effective-tier derivation
// shared between Matches (tier chips) and any other consumer. Previously these
// constants + the effectiveTier() function were inlined in Matches.tsx; pulling
// them here keeps the ≥80 / 60–79 / <60 thresholds and the Expired/AutoDiscarded
// pseudo-tier semantics defined once.

import type { Match, FitTier, EvalTier } from './types';

export const FIT_TIER_LABELS: Record<EvalTier, { label: string; subtitle: string; description: string }> = {
  Primary: {
    label: 'Strong fit',
    subtitle: '≥ 80',
    description: 'Score 4.0+ / 5 — your CV clearly matches the role. Apply with confidence.',
  },
  Secondary: {
    label: 'Worth a look',
    subtitle: '60–79',
    description: 'Score 3.0–3.9 / 5 — borderline. Skim the JD before deciding to apply.',
  },
  Adjacent: {
    label: 'Likely skip',
    subtitle: '< 60',
    description: 'Score below 3.0 / 5 — off-domain or seniority mismatch. Apply only if you have a specific reason.',
  },
  Expired: {
    label: 'Expired',
    subtitle: 'closed',
    description: 'Posting is no longer live (daily liveness sweep marked it expired). Click to inspect this category in isolation.',
  },
  AutoDiscarded: {
    label: 'Auto-discarded',
    subtitle: 'Legacy pre-screen',
    description: 'Legacy reports auto-discarded by the LLM pre-screen (removed 2026-05-19). Delete a report to push the URL back into Pending and re-evaluate.',
  },
};

// Chinese labels for the UK-CN product. Kept as a parallel table rather than
// threading a locale through every consumer: the English export stays the
// default so nothing that already imports it changes behaviour.
export const FIT_TIER_LABELS_ZH: Record<EvalTier, { label: string; subtitle: string; description: string }> = {
  Primary: {
    label: '强匹配',
    subtitle: '≥ 80',
    description: '4.0 分以上（满分 5）——你的简历和岗位要求明显对得上，可以放心投。',
  },
  Secondary: {
    label: '值得一看',
    subtitle: '60–79',
    description: '3.0–3.9 分（满分 5）——处在边缘，建议先看一遍岗位描述再决定投不投。',
  },
  Adjacent: {
    label: '可能跳过',
    subtitle: '< 60',
    description: '低于 3.0 分（满分 5）——方向或层级不太对。除非你有特别的理由，否则不建议投。',
  },
  Expired: {
    label: '已过期',
    subtitle: '已关闭',
    description: '这个职位已经不再招了（每日检测标记为过期）。点击可以单独查看这一类。',
  },
  AutoDiscarded: {
    label: '自动丢弃',
    subtitle: '旧版预筛',
    description: '旧版本里被 LLM 预筛自动丢弃的报告（该功能已于 2026-05-19 移除）。删掉报告可以把链接放回待评估，重新评估一次。',
  },
};

/** Tier labels in the caller's language. */
export function fitTierLabels(zh: boolean) {
  return zh ? FIT_TIER_LABELS_ZH : FIT_TIER_LABELS;
}

// Real (score-based) fit tiers — what the server filters by default and what
// shows in the "All" view. Expired + AutoDiscarded are opt-in chips.
export const REAL_FIT_TIERS: FitTier[] = ['Primary', 'Secondary', 'Adjacent'];
export const ALL_TIERS: EvalTier[] = ['Primary', 'Secondary', 'Adjacent', 'Expired', 'AutoDiscarded'];

// Effective tier for a row. Mutually exclusive: Expired wins over AutoDiscarded
// wins over the real score-based fit tier. Kept in sync with the SQL CASE in
// web/lib/repos/reports.mjs listForMatches() — change both together.
export function effectiveTier(m: Match): EvalTier {
  if (m.liveness === 'expired') return 'Expired';
  if (m.status === 'auto-discarded') return 'AutoDiscarded';
  return m.fit;
}

// CVPicker's 0–100 fit-score bucket (distinct from the tier above).
export type ScoreBucket = 'strong' | 'worthALook' | 'likelySkip';
export function bucketScore(score: number): ScoreBucket {
  if (score >= 80) return 'strong';
  if (score >= 60) return 'worthALook';
  return 'likelySkip';
}
